import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowLeft } from "lucide-react";
import DeviceHeader from "@/components/device/DeviceHeader";
import DeviceTabs from "@/components/device/DeviceTabs";
import { type Device } from "@/hooks/useDevices";
import { getDeviceStatus } from "@/utils/deviceStatus";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { useState, useRef } from "react";
import * as devicesApi from "@/services/devicesApi";

const DeviceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getBackNavigation } = useNavigationContext();
  const [activeTab, setActiveTab] = useState("info");
  const tabsRef = useRef<HTMLDivElement>(null);
  
  const { data: device, isLoading, error } = useQuery({
    queryKey: ['device', id],
    queryFn: async () => {
      if (!id) throw new Error('Device ID is required');
      
      const detail = await devicesApi.getDeviceDetail(id);
      const d = detail as any;
      const deviceData: Device = {
        id: d.uuid || d.id || id,
        name: d.deviceName || d.name || 'Unknown Device',
        hostname: d.hostname || d.deviceName || '',
        os_type: d.osType || d.os_type || '',
        os_version: d.osVersion || d.os_version || '',
        status: getDeviceStatus(d.lastSeen || d.last_seen || '', d.status || 'unknown'),
        last_seen: d.lastSeen || d.last_seen || '',
        created_at: d.createdAt || d.created_at || '',
        serial_number: d.serialNumber || d.serial_number || null,
        ip_address: d.ipAddress || d.ip_address || null,
        battery_level: d.batteryLevel ?? d.battery_level ?? null,
        battery_status: d.batteryStatus ?? d.battery_status ?? null,
        installed_apps: d.installedApps || d.installed_apps || [],
        department: null,
        location: null,
        script_deployments: d.scriptDeployments || d.script_deployments || [],
      };
      return deviceData;
    },
    enabled: !!id,
    staleTime: 25000,
    retry: 1,
  });

  const handleBackClick = () => {
    const backNav = getBackNavigation();
    navigate(backNav?.path || '/devices');
  };

  const handleCardClick = (tab: string) => {
    setActiveTab(tab);
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (isLoading) {
    return <div className="min-h-screen bg-background p-6">
        <div className="container max-w-7xl mx-auto flex justify-center items-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>;
  }
  
  if (error || !device) {
    return <div className="min-h-screen bg-background p-6">
        <div className="container max-w-7xl mx-auto text-center text-destructive p-6">
          {error instanceof Error ? error.message : 'Error loading device details.'}
        </div>
      </div>;
  }
  
  const backNav = getBackNavigation();

  return <div className="h-full flex flex-col">
      <div className="border-b bg-card/30">
        <div className="flex items-center justify-between px-6 py-4">
          <Button variant="ghost" size="sm" onClick={handleBackClick}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backNav?.label || 'Back to Devices'}
          </Button>
        </div>
        <div className="px-6 pb-8 pt-4">
          <DeviceHeader device={device} onCardClick={handleCardClick} />
        </div>
      </div>
      <div className="flex-1 overflow-auto px-6 py-6">
        <div ref={tabsRef}>
          <DeviceTabs device={device} activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
      </div>
    </div>;
};

export default DeviceDetail;
